import { NotificationTypes } from '@accounts/accounts-alerts/accounts-alerts.interface';
import { Component, Input, OnChanges } from '@angular/core';
import { InfoItem, NavigationService } from '@frk/eds-components';
import { FavoritesService } from '@products/services/favorites.service';
import { IUserProfile } from '@services/profile.interface';
import { ProfileService } from '@services/profile.service';
import { SignInService } from '@services/sign-in.service';
import {
  FundNameData,
  FundSectionLink,
  FundShareClassId,
  ConfigurationId,
} from '@types';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PpssServiceFactory } from '@products/fund-listing/services/ppss.service.factory';
import { PpssService } from '@products/fund-listing/services/ppss.service';

// TODO: move to types folder
export interface PpssCardItem extends InfoItem {
  colId: string;
  hide?: boolean;
  rating?: number;
  hybridRating?: number;
  labelSmall?: boolean;
  additionalVal?: string;
}

export interface PpssCardSection {
  sectionId: string;
  sectionTitle?: string;
  sectionSubTitle?: string;
  isExpandable?: boolean; // indicates if this should be inside expand-collapse component
  items: PpssCardItem[];
  isEven?: boolean;
  sectionPlacement?: string;
  sectionToolTip?: string;
}

export interface PpssCard {
  cardId: string; // unique id for this card
  fundDetail: FundNameData;
  shareClassName: string;
  fundLink?: string; // link to fund detail page
  fundSectionLinks?: FundSectionLink[];
  sections: PpssCardSection[];
  downloadLabel?: string;
  downloadLink?: string; // link to file to download e.g. factsheet
  ppssMessage?: string;
  ppssTooltip?: string;
  showSalesCharges?: boolean;
  maxIntialSalesCharge?: string;
  cdsc?: string;
  isFavoriteFund?: boolean;
  hideFavorite?: boolean; // can override showing favorite button
}

@Component({
  selector: 'ft-ppss-card',
  templateUrl: './ppss-card.component.html',
  styleUrls: ['./ppss-card.component.scss'],
})
export class PpssCardComponent implements OnChanges {
  @Input() data: PpssCard;
  notificationType: NotificationTypes;
  fixedSections: PpssCardSection[] = [];
  expandableSections: PpssCardSection[] = [];
  active: boolean;
  isLoggedIn: boolean;
  fund: string;
  hasFavourite: boolean;
  ppssService: PpssService;

  private unsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    public navService: NavigationService,
    private favoritesService: FavoritesService,
    private profileService: ProfileService,
    private signInService: SignInService,
    private ppssServiceFactory: PpssServiceFactory
  ) {
    this.notificationType = NotificationTypes.ALERT;
    this.ppssService = this.ppssServiceFactory.getPpssService();

    this.profileService
      .getUserProfile$()
      ?.pipe(takeUntil(this.unsubscribe$))
      .subscribe((profile: IUserProfile) => {
        this.isLoggedIn = profile?.isLoggedIn;
      });
  }

  ngOnChanges(): void {
    this.fund = this.data.cardId;
    this.active = this.data.isFavoriteFund;

    this.hasFavourite =
      this.favoritesService.isFavouriteEnabled() && !this.data.hideFavorite;

    if (this.data.sections) {
      this.data.sections.map((data, index) => (data.isEven = index % 2 === 0));
      this.fixedSections = this.data.sections.filter(
        (section: PpssCardSection): boolean => !section.isExpandable
      );
      this.expandableSections = this.data.sections.filter(
        (section: PpssCardSection): boolean => section.isExpandable
      );

      if (
        this.ppssService.getProductType() === ConfigurationId.VIP &&
        !this.isLoggedIn
      ) {
        this.expandableSections.forEach((section: PpssCardSection) => {
          delete section.sectionTitle;
          delete section.sectionSubTitle;
        });
      }
    }
  }

  toggle() {
    if (this.isLoggedIn) {
      this.active = !this.active;
      this.favoritesService.toggleFavorite(this.fund as FundShareClassId);
    } else {
      this.signInService.showSignInModal();
    }
  }
}
